import { useState, useEffect } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToContent = () => {
    window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled || mobileMenuOpen ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="mx-auto px-5 md:px-10 lg:px-[80px] xl:px-[120px] py-4 md:py-6 flex items-center justify-between">
          <div className={`text-xl md:text-2xl font-bold tracking-tight transition-colors ${scrolled || mobileMenuOpen ? 'text-[#0A1628]' : 'text-white'}`}>
            DELKYS
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-6 lg:gap-10 xl:gap-12">
            {['About', 'Business', 'Brands', 'Experience', 'Community', 'Company'].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase()}`}
                className={`text-sm font-medium transition-colors hover:text-[#D4AF37] whitespace-nowrap ${scrolled ? 'text-[#0A1628]' : 'text-white'}`}
              >
                {item}
              </a>
            ))}
          </div>

          {/* Hamburger Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-8 h-8 flex items-center justify-center cursor-pointer transition-colors ${scrolled || mobileMenuOpen ? 'text-[#0A1628]' : 'text-white'}`}
            aria-label="Toggle menu"
          >
            <i className={`text-2xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-5 py-4 flex flex-col gap-4">
            {['About', 'Business', 'Brands', 'Experience', 'Community', 'Company'].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase()}`}
                onClick={closeMobileMenu}
                className="text-base font-medium text-[#0A1628] hover:text-[#D4AF37] transition-colors py-1 cursor-pointer"
              >
                {item}
              </a>
            ))}
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative h-screen w-full overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://public.readdy.ai/ai/img_res/8767af424e9ec80469f79aa4b044ff84.jpg"
            alt="DELKYS Heritage"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628]/90 via-[#0A1628]/85 to-[#0A1628]/75"></div>

        <div className="relative h-full flex items-center">
          <div className="mx-auto px-5 md:px-10 lg:px-[80px] xl:px-[120px] w-full">
            <div className="max-w-[700px]">
              <h1 className="font-serif break-keep">
                <div className="text-[60px] sm:text-[80px] md:text-[100px] lg:text-[120px] leading-[1.1] font-bold text-white mb-3 md:mb-4">DELKYS</div>
                <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-tight font-medium text-white tracking-[1px] md:tracking-[2px] mb-2 md:mb-3">
                  30년 맛으로 기억되는 브랜드
                </div>
                <div className="text-lg sm:text-xl md:text-2xl lg:text-[28px] leading-relaxed font-light text-[#D4AF37]">A Legacy of Flavor</div>
              </h1>

              <p className="mt-8 md:mt-12 lg:mt-[60px] text-sm sm:text-base lg:text-lg leading-[1.8] text-white/80 max-w-[520px] break-keep">
                신뢰받는 브랜드와 혁신적인 콘셉트를 통해 잊을 수 없는 식음료 경험을 제공합니다.
              </p>

              <button className="mt-10 md:mt-16 lg:mt-20 px-8 md:px-10 lg:px-12 py-3 md:py-4 bg-white text-[#0A1628] rounded-full text-sm md:text-base font-semibold hover:bg-transparent hover:text-white hover:border-2 hover:border-[#D4AF37] transition-all duration-300 flex items-center gap-3 group whitespace-nowrap cursor-pointer">
                Discover Our Brands
                <i className="ri-arrow-right-line text-lg md:text-xl group-hover:translate-x-1 transition-transform"></i>
              </button>
            </div>
          </div>
        </div>

        <button
          onClick={scrollToContent}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white animate-bounce cursor-pointer"
          aria-label="Scroll to content"
        >
          <i className="ri-arrow-down-line text-2xl md:text-3xl"></i>
        </button>
      </section>

      {/* About DELKYS */}
      <section id="about" className="bg-white py-20 md:py-28 lg:py-40">
        <div className="mx-auto px-5 md:px-10 lg:px-[80px] xl:px-[120px]">
          <div className="flex flex-col lg:grid lg:grid-cols-[40%_60%] gap-10 md:gap-14 lg:gap-20 items-center">
            {/* Image */}
            <div className="w-full">
              <div className="rounded-2xl lg:rounded-3xl overflow-hidden h-[260px] sm:h-[360px] md:h-[440px] lg:h-[600px] w-full">
                <img
                  src="https://static.readdy.ai/image/34ff3cd7eed362d71b5533d44f73e2ec/222301a748d6de7cefc0f292bf228479.png"
                  alt="DELKYS Store"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Text */}
            <div className="lg:pr-20 break-keep">
              <div className="text-xs font-semibold tracking-[3px] text-[#0A1628] uppercase mb-6 md:mb-8 lg:mb-10">About DELKYS</div>

              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-[56px] leading-[1.2] font-bold text-[#0A1628] mb-10 md:mb-14 lg:mb-20 break-keep">
                고객에게 사랑받는<br />미식 브랜드 DELKYS
              </h2>

              <div className="max-w-[580px]">
                <p className="text-sm sm:text-base lg:text-lg leading-[1.9] text-[#333333] mb-5 break-keep">
                  DELKYS는 30년 이상의 경험을 바탕으로 다양한 식음료 브랜드를 기획하고 운영해온 전문 브랜드 기업입니다.
                </p>
                <p className="text-sm sm:text-base lg:text-lg leading-[1.9] text-[#333333] break-keep">
                  시그니처 스트리트 푸드부터 현대적인 디저트 브랜드까지, DELKYS는 독창적인 다이닝 경험과 기억에 남는 맛을 지속적으로 선보이고 있습니다.
                </p>
              </div>

              <div className="flex items-start gap-8 sm:gap-12 md:gap-16 lg:gap-20 mt-10 md:mt-12 lg:mt-[60px] flex-wrap">
                <div>
                  <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-2">30+</div>
                  <div className="text-xs sm:text-sm text-gray-500 uppercase tracking-wide">Years</div>
                </div>
                <div>
                  <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-2">Multiple</div>
                  <div className="text-xs sm:text-sm text-gray-500 uppercase tracking-wide">Brands</div>
                </div>
                <div>
                  <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-2">Countless</div>
                  <div className="text-xs sm:text-sm text-gray-500 uppercase tracking-wide">Experiences</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Business */}
      <section id="business" className="bg-[#F8F7F5] py-16 md:py-24 lg:py-[140px]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-12">
          <div className="text-center mb-12 md:mb-16 lg:mb-[100px]">
            <div className="text-xs font-semibold tracking-[3px] text-[#0A1628] uppercase mb-4 md:mb-6">Our Business</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] break-keep">다양한 식음 비즈니스</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                icon: 'ri-restaurant-2-line',
                title: 'Food',
                description: '시그니처 스트리트 푸드 콘셉트와 진정성 있는 미식 경험으로 품미의 본질을 담아냅니다.'
              },
              {
                icon: 'ri-cake-3-line',
                title: 'Ice Cream',
                description: '프리미엄 재료와 혁신적인 레시피로 완성한 수제 젤라토와 현대적인 디저트 브랜드를 선보입니다.'
              },
              {
                icon: 'ri-cup-line',
                title: 'Beverage',
                description: '현대적인 감각에 맞춘 상쾌한 음료 콘셉트와 스페셜티 음료를 제공합니다.'
              }
            ].map((business, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl overflow-hidden hover:-translate-y-2 transition-all duration-300 group cursor-pointer"
              >
                <div className="h-[200px] sm:h-[240px] lg:h-[280px] bg-gradient-to-br from-[#0A1628]/10 to-[#0A1628]/5 flex items-center justify-center group-hover:from-[#D4AF37]/20 group-hover:to-[#D4AF37]/10 transition-all duration-300">
                  <i className={`${business.icon} text-[60px] lg:text-[80px] text-[#0A1628] group-hover:text-[#D4AF37] transition-colors`}></i>
                </div>
                <div className="p-7 md:p-8 lg:p-10">
                  <h3 className="text-2xl lg:text-[28px] font-bold text-[#0A1628] mb-3 md:mb-5">{business.title}</h3>
                  <p className="text-sm md:text-base leading-[1.7] text-gray-600 break-keep">{business.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Brands */}
      <section id="brands" className="bg-white py-16 md:py-24 lg:pt-[180px] lg:pb-[160px]">
        <div className="mx-auto px-5 md:px-10 lg:px-[80px] xl:px-[120px]">
          <div className="mb-12 md:mb-16 lg:mb-[120px]">
            <div className="text-xs font-semibold tracking-[3px] text-[#0A1628] uppercase mb-4 md:mb-6">Our Brands</div>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-[64px] font-bold text-[#0A1628] mb-3 md:mb-4 break-keep">열정으로 만든 브랜드</h2>
            <p className="text-sm md:text-base lg:text-lg text-gray-500 break-keep">각 브랜드는 맛과 혁신에 대한 고유한 이야기를 담고 있습니다</p>
          </div>

          {/* Brand 1 - DELKYS Kebab */}
          <div className="flex flex-col lg:grid lg:grid-cols-[55%_45%] gap-10 md:gap-12 lg:gap-16 items-center mb-14 md:mb-20 lg:mb-[100px]">
            <div className="rounded-2xl lg:rounded-[20px] overflow-hidden h-[260px] sm:h-[360px] lg:h-[500px] w-full">
              <img
                src="https://readdy.ai/api/search-image?query=premium%20gourmet%20kebab%20wrap%20with%20fresh%20vegetables%20and%20grilled%20meat%20on%20elegant%20white%20plate%2C%20professional%20food%20photography%2C%20clean%20white%20background%20with%20subtle%20shadows%2C%20high-end%20street%20food%20presentation%2C%20appetizing%20close-up%20shot%2C%20modern%20culinary%20styling&width=800&height=500&seq=delkys-kebab-brand&orientation=landscape"
                alt="DELKYS Kebab"
                className="w-full h-full object-cover object-center"
              />
            </div>

            <div className="lg:pl-8 break-keep">
              <h3 className="text-2xl sm:text-3xl lg:text-[40px] font-bold text-[#0A1628] mb-3 md:mb-4">DELKYS Kebab</h3>
              <div className="w-[60px] h-[2px] bg-[#D4AF37] mb-6 md:mb-8"></div>
              <p className="text-sm md:text-base leading-[1.8] text-gray-600 mb-8 md:mb-10 break-keep">
                DELKYS의 시그니처 케밥 브랜드는 정통 품미와 프리미엄 재료를 한 입에 담아냅니다. 테마파크를 포함한 주요 명소에서 운영되며, DELKYS Kebab은 고품질 스트리트 푸드의 대명사로 자리잡았습니다.
              </p>
              <a href="#" className="text-sm font-medium text-[#0A1628] underline hover:text-[#D4AF37] transition-colors inline-flex items-center gap-2 cursor-pointer whitespace-nowrap">
                Explore Brand
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>

          {/* Brand 2 - JjalJjeupbang Gelato */}
          <div className="flex flex-col-reverse lg:grid lg:grid-cols-[45%_55%] gap-10 md:gap-12 lg:gap-16 items-center">
            <div className="lg:pr-8 break-keep">
              <h3 className="text-2xl sm:text-3xl lg:text-[40px] font-bold text-[#0A1628] mb-3 md:mb-4">JjalJjeupbang Gelato</h3>
              <div className="w-[60px] h-[2px] bg-[#D4AF37] mb-6 md:mb-8"></div>
              <p className="text-sm md:text-base leading-[1.8] text-gray-600 mb-8 md:mb-10 break-keep">
                전통 젤라토 장인 정신과 혁신적인 한국 품미를 결합한 현대적인 디저트 브랜드입니다. JjalJjeupbang은 여러 지점에서 고객들에게 사랑받는 수제 냉동 디저트를 선보이고 있습니다.
              </p>
              <a href="#" className="text-sm font-medium text-[#0A1628] underline hover:text-[#D4AF37] transition-colors inline-flex items-center gap-2 cursor-pointer whitespace-nowrap">
                Explore Brand
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            <div className="rounded-2xl lg:rounded-[20px] overflow-hidden h-[260px] sm:h-[360px] lg:h-[500px] w-full">
              <img
                src="https://readdy.ai/api/search-image?query=artisanal%20gelato%20ice%20cream%20scoops%20in%20elegant%20glass%20bowl%20with%20fresh%20fruit%20garnish%2C%20premium%20dessert%20presentation%20on%20marble%20surface%2C%20clean%20white%20background%2C%20professional%20food%20photography%2C%20colorful%20gelato%20flavors%2C%20modern%20dessert%20styling%2C%20appetizing%20close-up&width=800&height=500&seq=jjaljjeupbang-gelato&orientation=landscape"
                alt="JjalJjeupbang Gelato"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Brand Experience */}
      <section id="experience" className="bg-[#0A1628] py-16 md:py-24 lg:py-[200px]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-12">
          <div className="text-center mb-12 md:mb-16 lg:mb-[120px]">
            <div className="text-xs font-semibold tracking-[3px] text-[#D4AF37] uppercase mb-5 md:mb-8">Brand Experience</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-[56px] leading-[1.3] text-white mb-2 break-keep">맛과 공간이 만나는</h2>
            <p className="font-serif text-3xl sm:text-4xl lg:text-[56px] leading-[1.3] text-white/70 break-keep">특별한 경험</p>
          </div>

          <div className="space-y-4 md:space-y-6 mb-12 md:mb-16 lg:mb-[100px]">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
              <div className="rounded-xl overflow-hidden h-[200px] sm:h-[280px] lg:h-[400px] w-full group cursor-pointer">
                <img
                  src="https://readdy.ai/api/search-image?query=close-up%20macro%20shot%20of%20gourmet%20food%20dish%20with%20beautiful%20plating%20and%20garnish%2C%20professional%20culinary%20presentation%2C%20warm%20ambient%20lighting%2C%20shallow%20depth%20of%20field%2C%20appetizing%20food%20photography%2C%20premium%20restaurant%20quality&width=400&height=400&seq=experience-food-1&orientation=squarish"
                  alt="Food Experience"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="rounded-xl overflow-hidden h-[200px] sm:h-[280px] lg:h-[400px] w-full group cursor-pointer">
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20elegant%20restaurant%20interior%20with%20customers%20enjoying%20meals%2C%20warm%20ambient%20lighting%2C%20contemporary%20dining%20atmosphere%2C%20people%20dining%20together%2C%20cozy%20food%20establishment%20ambiance%2C%20professional%20hospitality%20photography&width=400&height=400&seq=experience-atmosphere-1&orientation=squarish"
                  alt="Atmosphere"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="rounded-xl overflow-hidden h-[200px] sm:h-[280px] lg:h-[400px] w-full group cursor-pointer">
                <img
                  src="https://readdy.ai/api/search-image?query=happy%20customers%20smiling%20while%20enjoying%20delicious%20food%20at%20restaurant%2C%20authentic%20dining%20moment%2C%20genuine%20customer%20satisfaction%2C%20warm%20friendly%20atmosphere%2C%20candid%20food%20experience%20photography%2C%20people%20sharing%20meals&width=400&height=400&seq=experience-customers-1&orientation=squarish"
                  alt="Customer Experience"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-[60%_40%] gap-4 md:gap-6">
              <div className="rounded-xl overflow-hidden h-[200px] sm:h-[260px] lg:h-[320px] w-full group cursor-pointer">
                <img
                  src="https://readdy.ai/api/search-image?query=wide%20shot%20of%20premium%20food%20brand%20store%20exterior%20and%20interior%20design%2C%20modern%20commercial%20space%2C%20elegant%20branding%20elements%2C%20professional%20retail%20photography%2C%20clean%20contemporary%20architecture%2C%20inviting%20storefront%20atmosphere&width=700&height=320&seq=experience-store-1&orientation=landscape"
                  alt="Store Design"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="rounded-xl overflow-hidden h-[200px] sm:h-[260px] lg:h-[320px] w-full group cursor-pointer">
                <img
                  src="https://readdy.ai/api/search-image?query=detailed%20shot%20of%20premium%20food%20brand%20packaging%20and%20product%20presentation%2C%20elegant%20brand%20identity%20elements%2C%20sophisticated%20design%20details%2C%20professional%20product%20photography%2C%20clean%20minimalist%20styling&width=400&height=320&seq=experience-details-1&orientation=portrait"
                  alt="Brand Details"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>
          </div>

          <div className="text-center">
            <p className="text-sm sm:text-base lg:text-lg leading-[1.9] text-white/85 max-w-[700px] mx-auto break-keep">
              DELKYS 경험의 모든 접점은 탁월함을 전달하기 위해 세심하게 설계되었습니다. 첫 한 입부터 오래 남는 인상까지, 우리는 진정성과 품질, 그리고 훌륭한 음식에 대한 열정으로 특별한 순간을 만들어갑니다.
            </p>
          </div>
        </div>
      </section>

      {/* Closing Statement */}
      <section className="bg-white py-20 md:py-28 lg:py-[200px]">
        <div className="max-w-[900px] mx-auto px-5 md:px-12 text-center">
          <h2 className="font-serif text-[56px] sm:text-[72px] md:text-[88px] lg:text-[96px] font-bold text-[#0A1628] tracking-tight mb-10 md:mb-14 lg:mb-[60px]">DELKYS</h2>

          <div className="w-[80px] md:w-[120px] h-[1px] bg-[#D4AF37] mx-auto mb-10 md:mb-14 lg:mb-[60px]"></div>

          <p className="font-serif text-xl sm:text-2xl md:text-[28px] lg:text-[32px] leading-[1.5] text-[#0A1628] font-medium mb-8 md:mb-12 break-keep">
            경험, 열정, 그리고 맛으로 쌓아온 브랜드
          </p>

          <p className="text-base md:text-lg lg:text-xl leading-[1.7] text-gray-600 max-w-[700px] mx-auto break-keep">
            30년 이상 식문화와 브랜드 혁신에 헌신해온 DELKYS의 이야기
          </p>

          <button className="mt-12 md:mt-16 lg:mt-20 px-10 md:px-12 lg:px-14 py-4 md:py-5 bg-[#0A1628] text-white rounded-full text-sm md:text-base font-semibold hover:bg-transparent hover:text-[#0A1628] hover:border-2 hover:border-[#D4AF37] transition-all duration-300 inline-flex items-center gap-3 group whitespace-nowrap cursor-pointer">
            Partner With Us
            <i className="ri-arrow-right-line text-lg md:text-xl group-hover:translate-x-1 transition-transform"></i>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A1628] pt-14 md:pt-20 lg:pt-[100px] pb-8 md:pb-10 lg:pb-[60px]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[30%_40%_30%] gap-10 md:gap-14 lg:gap-20 mb-12 md:mb-16 lg:mb-20">
            {/* Left Column */}
            <div>
              <div className="text-3xl lg:text-4xl font-bold text-white mb-5 md:mb-8 lg:mb-10">DELKYS</div>
              <p className="text-sm md:text-base text-white/70 mb-8 md:mb-10 lg:mb-[60px]">Excellence in Every Bite</p>

              <div className="flex items-center gap-4 md:gap-5">
                {['ri-instagram-line', 'ri-facebook-fill', 'ri-youtube-fill', 'ri-linkedin-fill'].map((icon) => (
                  <a key={icon} href="#" className="w-9 h-9 md:w-10 md:h-10 rounded-full border-2 border-white/30 flex items-center justify-center text-white hover:bg-[#D4AF37] hover:border-[#D4AF37] transition-all cursor-pointer">
                    <i className={`${icon} text-base md:text-lg`}></i>
                  </a>
                ))}
              </div>
            </div>

            {/* Middle Column */}
            <div>
              <h3 className="text-xs font-semibold tracking-[2px] text-white uppercase mb-6 md:mb-8">Contact</h3>
              <div className="text-sm md:text-base leading-[1.8] text-white/85 mb-6 md:mb-10">
                Seoul, South Korea<br />
                Food & Beverage Excellence
              </div>
              <div className="text-sm md:text-base text-white/85 mb-4 md:mb-5">T. +82-2-XXXX-XXXX</div>
              <a href="mailto:info@delkys.com" className="text-sm md:text-base text-white/85 hover:text-[#D4AF37] transition-colors cursor-pointer">info@delkys.com</a>
            </div>

            {/* Right Column */}
            <div>
              <h3 className="text-xs font-semibold tracking-[2px] text-white uppercase mb-6 md:mb-8">Quick Links</h3>
              <div className="space-y-4 md:space-y-6">
                {[
                  { label: 'About Us', href: '#about' },
                  { label: 'Our Brands', href: '#brands' },
                  { label: 'Business Inquiry', href: '#' },
                  { label: 'Careers', href: '#' },
                ].map((link) => (
                  <a key={link.label} href={link.href} className="block text-sm md:text-base text-white/85 hover:text-[#D4AF37] transition-colors cursor-pointer whitespace-nowrap">
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="pt-6 md:pt-8 border-t border-white/20">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-xs md:text-sm text-white/60">© 2025 DELKYS. All rights reserved.</p>
              <div className="flex items-center gap-4 md:gap-6">
                <a href="#" className="text-xs md:text-sm text-white/60 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Privacy Policy</a>
                <span className="text-white/40">|</span>
                <a href="#" className="text-xs md:text-sm text-white/60 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Terms of Service</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
